import type { Asset, AudioLibraryResponse, Composition, Job, Project, Scene } from '@shared/types'
import { DEFAULT_SECTION_SEQUENCE } from '@shared/colorGrades'
import { makeId } from '../lib/id'

/**
 * VITE_MOCK=1 fixtures: 3 assets x 5 scenes, 1 composition, so the whole UI
 * (asset bin -> drag to timeline -> preview -> control panel -> export) can
 * be exercised with zero server. Video/thumb files are real, generated
 * placeholders under public/mock/ (see app/web/public/mock).
 */

const PROJECT_ID = 'proj_demo'

function scenesFor(assetId: string, durationSec: number, count = 5): Scene[] {
  const step = durationSec / count
  return Array.from({ length: count }, (_, i) => ({
    index: i,
    startSec: Number((i * step).toFixed(2)),
    endSec: Number(((i + 1) * step).toFixed(2)),
    thumbPath: `/mock/scenes/${assetId}-${i}.jpg`,
  }))
}

function makeAsset(id: string, filename: string, durationSec: number): Asset {
  return {
    id,
    projectId: PROJECT_ID,
    filename,
    mime: 'video/mp4',
    bytes: Math.round(durationSec * 900_000),
    durationSec,
    width: 540,
    height: 960,
    fps: 30,
    hasAudio: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    originalPath: `/mock/proxies/${id}.mp4`,
    proxyPath: `/mock/proxies/${id}.mp4`,
    thumbPath: `/mock/thumbs/${id}.jpg`,
    scenes: scenesFor(id, durationSec),
    ingestStatus: 'ready',
  }
}

export const MOCK_ASSETS: Asset[] = [
  makeAsset('asset-1', '銀座_金魚の回廊_01.mp4', 8),
  makeAsset('asset-2', '銀座_桜リウム_02.mp4', 7),
  makeAsset('asset-3', '名古屋_品種図鑑_03.mp4', 9),
]

export const MOCK_PROJECT: Project = {
  id: PROJECT_ID,
  name: 'デモプロジェクト（開幕告知型）',
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
  updatedAt: new Date().toISOString(),
  assetIds: MOCK_ASSETS.map((a) => a.id),
  compositionIds: ['comp_demo'],
}

export function makeEmptyComposition(projectId: string, name: string): Composition {
  return {
    id: makeId('comp'),
    projectId,
    name,
    version: 1,
    updatedAt: new Date().toISOString(),
    output: { width: 1080, height: 1920, fps: 30 },
    template: 'opening',
    seed: 1,
    sections: DEFAULT_SECTION_SEQUENCE.slice(0, 3).map((color, i) => ({
      id: makeId('sec'),
      name: ['猪目リウム', '花舞', '金魚の回廊'][i] ?? `セクション${i + 1}`,
      nameEn: ['Inome-rium', 'HANAMAI', 'Goldfish corridor'][i],
      color,
    })),
    tracks: { video: [], overlays: [], audio: [] },
    meta: {
      title: '夏のアートアクアリウム',
      seasonBadge: '初夏の企画展',
      venue: 'ginza',
      dateText: '2026.6.26 FRI - 9.23 WED',
      hashtags: ['#アートアクアリウム', '#金魚', '#銀座'],
    },
  }
}

export const MOCK_COMPOSITION: Composition = (() => {
  const comp = makeEmptyComposition(PROJECT_ID, '初稿')
  comp.id = 'comp_demo'
  const [secA, secB, secC] = comp.sections
  comp.tracks.video = [
    {
      id: makeId('seg'),
      assetId: 'asset-1',
      inSec: 0,
      outSec: 1.6,
      speed: 1,
      sectionId: secA.id,
      fit: 'cover',
      cropX: 0.5,
      transitionIn: 'cut',
    },
    {
      id: makeId('seg'),
      assetId: 'asset-2',
      inSec: 1.4,
      outSec: 2.8,
      speed: 1,
      sectionId: secB.id,
      fit: 'cover',
      cropX: 0.5,
      transitionIn: 'fade',
    },
    {
      id: makeId('seg'),
      assetId: 'asset-3',
      inSec: 1.8,
      outSec: 3.6,
      speed: 0.75,
      sectionId: secC.id,
      fit: 'cover',
      cropX: 0.5,
      transitionIn: 'cut',
    },
  ]
  comp.tracks.overlays = [
    {
      kind: 'title',
      id: makeId('ov'),
      startSec: 0,
      durationSec: 2,
      text: '夏のアートアクアリウム',
      emphasis: { char: '夏', color: '#E6B422', scale: 1.6 },
      vertical: true,
      seasonBadge: '初夏の企画展',
      dateText: '2026.6.26 FRI - 9.23 WED',
      venue: 'ginza',
      position: 'center',
    },
    {
      kind: 'label',
      id: makeId('ov'),
      startSec: 1.6,
      durationSec: 1.4,
      ja: '猪目リウム',
      en: 'Inome-rium',
      style: 'bracket',
      position: 'left-upper',
    },
    {
      kind: 'end',
      id: makeId('ov'),
      startSec: 5.2,
      durationSec: 2.0,
      lines: ['2026.6.26 FRI - 9.23 WED', 'アートアクアリウム銀座'],
      vertical: true,
      cta: '詳細はプロフィールから',
      venue: 'ginza',
    },
    // UI_MK2_PHASE2.md §2-8: mock fixture に FreeText が無かったので 2a が追加
    // （2b はこれを前提にテストを書ける）。startSec/durationSec は動画トラック
    // 全体（0〜5.4s）に重ねる。
    {
      kind: 'free',
      id: makeId('ov'),
      startSec: 0,
      durationSec: 5.4,
      text: 'サンプルテキスト',
      font: 'gothic',
      sizePx: 64,
      color: '#FFFFFF',
      x: 0.5,
      y: 0.58,
      vertical: false,
    },
  ]
  comp.tracks.audio = [
    {
      id: makeId('aud'),
      kind: 'bgm',
      assetId: null,
      builtinId: 'bgm-sample',
      startSec: 0,
      inSec: 0,
      outSec: null,
      gainDb: -6,
      fadeInSec: 0.5,
      fadeOutSec: 1.0,
    },
  ]
  return comp
})()

// ---------------------------------------------------------------------------
// P10-2 (プロジェクト/構成案の管理, 担当 B, docs/P10_PHASE1.md §3 item 11):
// "プロジェクト 2 件以上・うち 1 件は構成案 2 件" — appended-only, MOCK_PROJECT/
// MOCK_ASSETS/MOCK_COMPOSITION above are untouched (C also appends to this
// file for its own audio fixture needs, per the same task spec's shared-file
// rule).
// ---------------------------------------------------------------------------

const PROJECT_2_ID = 'proj_demo2'

function makeAsset2(id: string, filename: string, durationSec: number): Asset {
  return {
    id,
    projectId: PROJECT_2_ID,
    filename,
    mime: 'video/mp4',
    bytes: Math.round(durationSec * 900_000),
    durationSec,
    width: 540,
    height: 960,
    fps: 30,
    hasAudio: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 40).toISOString(),
    originalPath: `/mock/proxies/asset-1.mp4`,
    proxyPath: `/mock/proxies/asset-1.mp4`,
    thumbPath: `/mock/thumbs/asset-1.jpg`,
    scenes: scenesFor(id, durationSec),
    ingestStatus: 'ready',
  }
}

export const MOCK_ASSETS_2: Asset[] = [makeAsset2('asset-2-1', '名古屋_睡蓮の間_01.mp4', 8)]

export const MOCK_COMPOSITION_2A: Composition = (() => {
  const comp = makeEmptyComposition(PROJECT_2_ID, '初稿')
  comp.id = 'comp_demo2_a'
  comp.updatedAt = new Date(Date.now() - 1000 * 60 * 25).toISOString()
  return comp
})()

/**
 * mockClient.ts pins a permanently 'running' render Job to this Composition
 * at module init (not a timer-driven one via `runFakeJob`) so
 * `web/e2e/projects.spec.ts` (task spec §3 item 12) can deterministically
 * exercise the 409/disabled "busy" paths without racing a timeout.
 */
export const MOCK_COMPOSITION_2B: Composition = (() => {
  const comp = makeEmptyComposition(PROJECT_2_ID, '第2稿（書き出し中）')
  comp.id = 'comp_demo2_b'
  comp.updatedAt = new Date(Date.now() - 1000 * 60 * 5).toISOString()
  return comp
})()

export const MOCK_PROJECT_2: Project = {
  id: PROJECT_2_ID,
  name: 'デモプロジェクト2（体験訴求型）',
  createdAt: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
  updatedAt: MOCK_COMPOSITION_2B.updatedAt,
  assetIds: MOCK_ASSETS_2.map((a) => a.id),
  compositionIds: [MOCK_COMPOSITION_2A.id, MOCK_COMPOSITION_2B.id],
}

// ---------------------------------------------------------------------------
// P10-2 担当B (BGM ライブラリ, task spec §3 item 7): `mockClient.listAudioLibrary`
// fixture — 2 items per spec. `'bgm-sample'` keeps the SAME id
// `MOCK_COMPOSITION.tracks.audio[0].builtinId` already points at above (and
// that web/e2e/preview-audio.spec.ts / web/src/lib/{audioMath,timelineMath}.
// test.ts fixtures reference), so nothing that already resolves that id
// (URL construction, AudioInspector's display-name lookup) changes behavior —
// this file is appended-only (B/C shared-file rule).
// ---------------------------------------------------------------------------

export const MOCK_AUDIO_LIBRARY: AudioLibraryResponse = {
  dir: '/mock/bgm',
  items: [
    { id: 'bgm-sample', title: 'サンプルBGM（仮）', ext: 'mp3', durationSec: 12 },
    { id: 'bgm-calm', title: '静かな環境音（仮）', ext: 'mp3', durationSec: 30 },
  ],
}

// ---------------------------------------------------------------------------
// P10-3 担当B (出力サイズ切替, docs/P10_PHASE3.md §3 item 8): a project holding
// ONE Composition whose `output` is NOT one of shared/src/outputPresets.ts's
// 4 presets (720x1280) — `outputPresetIdFor()` must resolve this to 'custom'
// so TabExport's preset select shows the disabled "カスタム 720×1280" option
// instead of matching one of the 4 presets. A new project (not appended into
// MOCK_PROJECT/MOCK_PROJECT_2's existing `compositionIds` arrays — those are
// existing lines, untouched per this file's shared-file rule) — same pattern
// P10-2 used when it added MOCK_PROJECT_2 for its own fixture needs.
// ---------------------------------------------------------------------------

const PROJECT_3_ID = 'proj_demo3'

export const MOCK_COMPOSITION_CUSTOM: Composition = (() => {
  const comp = makeEmptyComposition(PROJECT_3_ID, 'カスタムサイズ')
  comp.id = 'comp_demo_custom'
  comp.updatedAt = new Date(Date.now() - 1000 * 60 * 20).toISOString()
  comp.output = { width: 720, height: 1280, fps: 30 }
  return comp
})()

export const MOCK_PROJECT_3: Project = {
  id: PROJECT_3_ID,
  name: 'デモプロジェクト3（カスタムサイズ）',
  createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
  updatedAt: MOCK_COMPOSITION_CUSTOM.updatedAt,
  assetIds: [],
  compositionIds: [MOCK_COMPOSITION_CUSTOM.id],
}

// ---------------------------------------------------------------------------
// P10-3 担当A (書き出し履歴, docs/P10_PHASE3.md §2 item 7): render Job fixtures
// for ExportHistory.tsx / web/e2e/export-history.spec.ts. 4 tied to
// MOCK_COMPOSITION (comp_demo) — 2 with `result` (P10-3's post-render
// snapshot), 1 `done` WITHOUT `result` (a pre-P10-3 "legacy" Job — the UI
// must show "—" for size/尺/容量, never crash on the missing field), 1
// `error` (no resultPath/result at all) — plus 1 more `done`+`result` Job
// tied to a DIFFERENT Composition (MOCK_COMPOSITION_2B, in the SAME project
// as MOCK_COMPOSITION_2A) so ExportHistory's「プロジェクト全体」scope has a
// row whose compositionName differs from whichever Composition is currently
// open. `mockClient.ts` seeds its in-memory `jobs` Map from this array at
// module init (same append-only convention as MOCK_PROJECT_3 above).
// ---------------------------------------------------------------------------

export const MOCK_RENDER_JOBS: Job[] = [
  {
    id: 'job_render_demo_1',
    kind: 'render',
    status: 'done',
    progress: 1,
    log: ['render job queued (mock)', 'render complete'],
    compositionId: MOCK_COMPOSITION.id,
    resultPath: '/mock/proxies/asset-1.mp4',
    createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 29).toISOString(),
    result: {
      compositionName: MOCK_COMPOSITION.name,
      output: { width: 1080, height: 1920, fps: 30 },
      durationSec: 5.4,
      bytes: 8_400_000,
    },
  },
  {
    id: 'job_render_demo_2',
    kind: 'render',
    status: 'done',
    progress: 1,
    log: ['render job queued (mock)', 'render complete'],
    compositionId: MOCK_COMPOSITION.id,
    resultPath: '/mock/proxies/asset-2.mp4',
    createdAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 14).toISOString(),
    result: {
      compositionName: MOCK_COMPOSITION.name,
      // Different preset than #1 (4:5) — demonstrates the size column
      // reflecting what THAT render actually was, independent of
      // MOCK_COMPOSITION's CURRENT `output` (1080x1920).
      output: { width: 1080, height: 1350, fps: 30 },
      durationSec: 5.4,
      bytes: 6_100_000,
    },
  },
  {
    id: 'job_render_demo_legacy',
    kind: 'render',
    status: 'done',
    progress: 1,
    log: ['render job queued (mock)', 'render complete'],
    compositionId: MOCK_COMPOSITION.id,
    resultPath: '/mock/proxies/asset-3.mp4',
    createdAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 89).toISOString(),
    // `result` deliberately absent — pre-P10-3 Job.
  },
  {
    id: 'job_render_demo_error',
    kind: 'render',
    status: 'error',
    progress: 0.4,
    log: ['render job queued (mock)', 'render started'],
    compositionId: MOCK_COMPOSITION.id,
    error: 'ffmpeg exited with code 1 (mock)',
    createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 44).toISOString(),
  },
  {
    id: 'job_render_2b',
    kind: 'render',
    status: 'done',
    progress: 1,
    log: ['render job queued (mock)', 'render complete'],
    compositionId: MOCK_COMPOSITION_2B.id,
    resultPath: '/mock/proxies/asset-1.mp4',
    createdAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 34).toISOString(),
    result: {
      compositionName: MOCK_COMPOSITION_2B.name,
      output: { width: 1080, height: 1920, fps: 30 },
      durationSec: 4,
      bytes: 5_000_000,
    },
  },
]
